import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { router } from 'expo-router';
import { Pressable, StyleSheet, Text, View, ScrollView } from 'react-native';

import { Fonts } from '@/constants/theme';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';

export default function AdminAccountScreen() {
  const { profile } = useAuth();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    router.replace('/sign-in');
  };

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'A';
  };

  return (
    <View style={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        
        {/* Profile Header Card */}
        <View style={styles.profileHeader}>
          <View style={styles.avatarContainer}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{getInitials(profile?.full_name || 'Admin User')}</Text>
            </View>
            <View style={styles.editBadge}>
              <MaterialIcons name="camera-alt" size={14} color="white" />
            </View>
          </View>
          
          <Text style={styles.profileName}>{profile?.full_name || 'Administrator'}</Text>
          <Text style={styles.profileEmail}>{profile?.email}</Text>
          
          <View style={styles.roleBadge}>
            <Text style={styles.roleText}>ADMINISTRATOR</Text>
          </View>
        </View>

        {/* Account Sections */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>PERSONAL INFORMATION</Text>
          <View style={styles.infoCard}>
            <InfoItem icon="person-outline" label="Full Name" value={profile?.full_name || 'Not provided'} />
            <View style={styles.divider} />
            <InfoItem icon="mail-outline" label="Email Address" value={profile?.email || 'No email linked'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SCHOOL DETAILS</Text>
          <View style={styles.infoCard}>
            <InfoItem
              icon="domain"
              label="Institution"
              value={profile?.school_name || 'School workspace'}
            />
            <View style={styles.divider} />
            <InfoItem icon="fingerprint" label="Admin ID" value={profile?.school_id?.slice(0, 8).toUpperCase() || 'ADM-8273'} />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SECURITY & PREFERENCES</Text>
          <View style={styles.infoCard}>
            <Pressable style={styles.menuItem}>
              <View style={styles.menuLeft}>
                <View style={[styles.menuIconBg, { backgroundColor: '#E3F2FD' }]}>
                  <MaterialIcons name="lock-outline" size={20} color="#1976D2" />
                </View>
                <Text style={styles.menuText}>Change Password</Text>
              </View>
              <MaterialIcons name="chevron-right" size={20} color="#BDBDBD" />
            </Pressable>
            
            <View style={styles.divider} />
            
            <Pressable style={styles.menuItem}>
              <View style={styles.menuLeft}>
                <View style={[styles.menuIconBg, { backgroundColor: '#F3E5F5' }]}>
                  <MaterialIcons name="notifications-none" size={20} color="#7B1FA2" />
                </View>
                <Text style={styles.menuText}>Push Notifications</Text>
              </View>
              <MaterialIcons name="chevron-right" size={20} color="#BDBDBD" />
            </Pressable>
          </View>
        </View>

        <Pressable onPress={handleSignOut} style={styles.logoutBtn}>
          <MaterialIcons name="logout" size={20} color="#D32F2F" />
          <Text style={styles.logoutText}>Sign Out Account</Text>
        </Pressable>

        <Text style={styles.versionText}>MCQGen v1.0.4 • Beta Access</Text>
        <View style={{ height: 100 }} />
      </ScrollView>
    </View>
  );
}

function InfoItem({ icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <View style={styles.infoItem}>
      <View style={styles.infoIconBg}>
        <MaterialIcons name={icon} size={18} color="#1B5E20" />
      </View>
      <View style={styles.infoTexts}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F7F5',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 30,
  },
  profileHeader: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 32,
    padding: 30,
    borderWidth: 0.5,
    borderColor: '#C8E6C9',
    marginBottom: 24,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: 16,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#1B5E20',
  },
  avatarText: {
    fontSize: 36,
    fontWeight: '800',
    color: '#1B5E20',
    fontFamily: Fonts.rounded,
  },
  editBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: '#1B5E20',
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: 'white',
  },
  profileName: {
    fontSize: 22,
    fontWeight: '800',
    color: '#1B5E20',
    fontFamily: Fonts.rounded,
    marginBottom: 4,
  },
  profileEmail: {
    fontSize: 14,
    color: 'rgba(46, 125, 50, 0.5)',
    marginBottom: 16,
  },
  roleBadge: {
    backgroundColor: '#1B5E20',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 99,
  },
  roleText: {
    color: 'white',
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 1,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: '800',
    color: 'rgba(46, 125, 50, 0.5)',
    letterSpacing: 2,
    marginLeft: 12,
    marginBottom: 10,
  },
  infoCard: {
    backgroundColor: 'white',
    borderRadius: 24,
    padding: 12,
    borderWidth: 0.5,
    borderColor: '#C8E6C9',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 16,
  },
  infoIconBg: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F1F8F1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoTexts: {
    flex: 1,
    gap: 2,
  },
  infoLabel: {
    fontSize: 10,
    color: 'rgba(46, 125, 50, 0.4)',
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 15,
    color: '#1C1C1E',
    fontWeight: '600',
  },
  divider: {
    height: 1,
    backgroundColor: '#F5F7F5',
    marginHorizontal: 12,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
  },
  menuLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  menuIconBg: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuText: {
    fontSize: 15,
    color: '#1C1C1E',
    fontWeight: '600',
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFEBEE',
    padding: 16,
    borderRadius: 20,
    gap: 10,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  logoutText: {
    fontSize: 15,
    fontWeight: '800',
    color: '#D32F2F',
  },
  versionText: {
    textAlign: 'center',
    fontSize: 12,
    color: '#BDBDBD',
    marginTop: 24,
  },
});
